import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Input } from '@react-apps/design-system';
import type {
  BillingAgreement,
  Invoice,
  TransactionOfferAndArrangements,
} from '../../domain/entities/sales.entity';
import {
  useCreateDebtArrangementDraft,
  useUpdateDebtArrangementDraft,
  useActivateDebtArrangement,
  useCancelDebtArrangement,
} from '../hooks/use-sales-actions';
import {
  InvoiceStatusBadge,
  formatCurrency,
  getApiErrorMessage,
} from '../shared/display';

const SAVE_DEBOUNCE_MS = 800;

function snapshot(invoiceIds: string[], reason: string): string {
  return JSON.stringify({ invoiceIds: [...invoiceIds].sort(), reason });
}

function formatSavedAt(iso: string): string {
  const d = new Date(iso);
  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
  const date = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  return `${time} - ${date}`;
}

type Step = 'edit' | 'conclude' | 'cancel';

/**
 * Modal de renegociação: edita o rascunho com autosave (debounce + "último
 * save"); a 1ª gravação válida cria o DA (DRAFT) e as seguintes atualizam.
 * "Concluir" ativa (DRAFT → WAITING_PAYMENT); "Cancelar" cancela o rascunho.
 * Tudo em um único modal (passos internos).
 */
export function RenegotiationModal({
  invalidateKey,
  billingAgreement,
  chain,
  chainLoading,
  invoicesData,
  invoicesLoading,
  open,
  onClose,
}: {
  invalidateKey: readonly unknown[];
  billingAgreement: BillingAgreement;
  chain: TransactionOfferAndArrangements | undefined;
  chainLoading: boolean;
  invoicesData: { invoices: Invoice[] } | undefined;
  invoicesLoading: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const ba = billingAgreement;
  const createDraft = useCreateDebtArrangementDraft(invalidateKey);
  const updateDraft = useUpdateDebtArrangementDraft();
  const activate = useActivateDebtArrangement(invalidateKey);
  const cancel = useCancelDebtArrangement(invalidateKey);

  const invoices = (invoicesData?.invoices ?? []).filter(
    (i) => i.billingAgreementId === ba.id,
  );
  const draftDa =
    chain?.debtArrangements.find(
      (d) => d.replacedBillingAgreement?.id === ba.id && d.status === 'draft',
    ) ?? null;

  const [step, setStep] = useState<Step>('edit');
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localCreatedId, setLocalCreatedId] = useState<string | null>(null);
  // Concluir (ativar)
  const [externalOfferId, setExternalOfferId] = useState('');
  const [externalProductId, setExternalProductId] = useState('');
  // Cancelar
  const [cancelReason, setCancelReason] = useState('');

  const initedRef = useRef(false);
  const savedSnapshotRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const effectiveDraftId = draftDa?.id ?? localCreatedId;

  // Init a cada abertura.
  useEffect(() => {
    if (!open) {
      initedRef.current = false;
      return;
    }
    if (initedRef.current) return;
    if (chainLoading || invoicesLoading || !chain || !invoicesData) return;
    initedRef.current = true;
    setStep('edit');
    setError(null);
    activate.reset();
    cancel.reset();
    setExternalOfferId('');
    setExternalProductId('');
    setCancelReason('');

    if (draftDa) {
      setReason(draftDa.reason);
      const sel: Record<string, boolean> = {};
      for (const inv of invoices) {
        sel[inv.id] = draftDa.sourceInvoiceIds.includes(inv.id);
      }
      setSelected(sel);
      setLastSavedAt(draftDa.updatedAt);
      setLocalCreatedId(draftDa.id);
      savedSnapshotRef.current = snapshot(
        draftDa.sourceInvoiceIds,
        draftDa.reason,
      );
    } else {
      const sel: Record<string, boolean> = {};
      for (const inv of invoices) {
        sel[inv.id] = inv.status === 'pending' || inv.status === 'overdue';
      }
      setSelected(sel);
      setReason('');
      setLastSavedAt(null);
      setLocalCreatedId(null);
      savedSnapshotRef.current = snapshot(
        Object.keys(sel).filter((k) => sel[k]),
        '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chainLoading, invoicesLoading, chain, invoicesData]);

  // Autosave (debounced) só no passo de edição.
  useEffect(() => {
    if (!open || !initedRef.current || step !== 'edit') return;
    const invoiceIds = invoices.filter((i) => selected[i.id]).map((i) => i.id);
    const snap = snapshot(invoiceIds, reason);
    if (snap === savedSnapshotRef.current) return;
    if (invoiceIds.length === 0 || !reason.trim()) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void save(invoiceIds, reason);
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, reason]);

  const save = async (invoiceIds: string[], reasonValue: string) => {
    setSaving(true);
    setError(null);
    try {
      if (!effectiveDraftId) {
        const created = await createDraft.mutateAsync({
          contractId: ba.contractId,
          replacedBillingAgreementId: ba.id,
          type: 'overdue_plus_future',
          invoiceIds,
          reason: reasonValue.trim(),
        });
        setLocalCreatedId(created.id);
      } else {
        await updateDraft.mutateAsync({
          debtArrangementId: effectiveDraftId,
          body: { invoiceIds, reason: reasonValue.trim() },
        });
      }
      savedSnapshotRef.current = snapshot(invoiceIds, reasonValue);
      setLastSavedAt(new Date().toISOString());
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao salvar o rascunho.'));
    } finally {
      setSaving(false);
    }
  };

  // Fecha o modal quando concluir/cancelar derem certo.
  useEffect(() => {
    if (activate.isSuccess || cancel.isSuccess) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate.isSuccess, cancel.isSuccess]);

  const handleConclude = () => {
    if (!effectiveDraftId) return;
    activate.mutate({
      debtArrangementId: effectiveDraftId,
      body: {
        externalOfferId: externalOfferId.trim(),
        externalProductId: externalProductId.trim() || undefined,
      },
    });
  };

  const handleCancelDraft = () => {
    if (!effectiveDraftId) return;
    cancel.mutate({
      debtArrangementId: effectiveDraftId,
      body: { reason: cancelReason || undefined },
    });
  };

  const savedLabel = saving
    ? 'Salvando…'
    : error
      ? error
      : lastSavedAt
        ? `Último save às ${formatSavedAt(lastSavedAt)}`
        : 'Rascunho não salvo — selecione ao menos 1 invoice e informe o motivo';

  const titleByStep: Record<Step, string> = {
    edit: 'Renegociação (rascunho)',
    conclude: 'Concluir renegociação',
    cancel: 'Cancelar renegociação',
  };
  const descByStep: Record<Step, string> = {
    edit: 'Edite o rascunho — as alterações são salvas automaticamente.',
    conclude:
      'Valida a offer de renegociação no Guru e tira do rascunho (DRAFT → WAITING_PAYMENT).',
    cancel: 'Cancela este rascunho de renegociação.',
  };

  const footer =
    step === 'edit' ? (
      <div className="flex w-full items-center justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button
          variant="danger"
          onClick={() => setStep('cancel')}
          disabled={!effectiveDraftId}
        >
          Cancelar renegociação
        </Button>
        <Button
          onClick={() => setStep('conclude')}
          disabled={!effectiveDraftId}
        >
          Concluir renegociação
        </Button>
      </div>
    ) : step === 'conclude' ? (
      <div className="flex w-full justify-end gap-2">
        <Button variant="secondary" onClick={() => setStep('edit')}>
          Voltar
        </Button>
        <Button
          onClick={handleConclude}
          disabled={activate.isPending || !externalOfferId.trim()}
        >
          {activate.isPending ? 'Concluindo…' : 'Concluir'}
        </Button>
      </div>
    ) : (
      <div className="flex w-full justify-end gap-2">
        <Button variant="secondary" onClick={() => setStep('edit')}>
          Voltar
        </Button>
        <Button
          variant="danger"
          onClick={handleCancelDraft}
          disabled={cancel.isPending}
        >
          {cancel.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
        </Button>
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleByStep[step]}
      description={descByStep[step]}
      size="lg"
      footer={footer}
    >
      {step === 'edit' && (
        <div className="space-y-4">
          <p
            className={[
              'text-xs',
              error
                ? 'text-red-600 dark:text-red-400'
                : 'text-(--color-text-secondary)',
            ].join(' ')}
          >
            {savedLabel}
          </p>

          <Input
            label="Motivo"
            id="reneg-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Justificativa da renegociação"
          />

          <div>
            <p className="text-(--color-text-secondary) mb-2 text-xs font-medium uppercase tracking-wide">
              Invoices a renegociar
            </p>
            {invoicesLoading ? (
              <p className="text-(--color-text-secondary) text-sm">
                Carregando…
              </p>
            ) : invoices.length === 0 ? (
              <p className="text-(--color-text-secondary) text-sm italic">
                Nenhuma invoice na Billing Agreement.
              </p>
            ) : (
              <ul className="space-y-2">
                {invoices.map((inv) => (
                  <li key={inv.id}>
                    <label className="border-(--color-border) flex items-center gap-3 rounded-lg border p-3 text-sm">
                      <input
                        type="checkbox"
                        checked={!!selected[inv.id]}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            [inv.id]: e.target.checked,
                          }))
                        }
                        className="h-4 w-4"
                      />
                      <span className="text-(--color-text-primary)">
                        Ciclo {inv.cycleIndex}
                      </span>
                      <InvoiceStatusBadge status={inv.status} />
                      <span className="text-(--color-text-secondary) ml-auto">
                        {formatCurrency(inv.value)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {step === 'conclude' && (
        <div className="space-y-4">
          {activate.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {getApiErrorMessage(activate.error, 'Falha ao concluir.')}
            </div>
          )}
          <Input
            label="External Offer ID"
            id="reneg-offer"
            value={externalOfferId}
            onChange={(e) => setExternalOfferId(e.target.value)}
            placeholder="ID da offer no gateway"
          />
          <Input
            label="External Product ID (opcional)"
            id="reneg-product"
            value={externalProductId}
            onChange={(e) => setExternalProductId(e.target.value)}
            placeholder="Herda o produto da BA renegociada se vazio"
          />
        </div>
      )}

      {step === 'cancel' && (
        <div className="space-y-4">
          {cancel.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {getApiErrorMessage(cancel.error, 'Falha ao cancelar.')}
            </div>
          )}
          <Input
            label="Motivo (opcional)"
            id="reneg-cancel-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Justificativa do cancelamento"
          />
        </div>
      )}
    </Modal>
  );
}
